import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';

export default function AdditionalConsumptionEditor({ customerId, date, entries, onSaved, onClose, onBusy }) {
  const { t } = useLanguage();
  const [quantity, setQuantity] = useState('1');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false);
  const request = useRef(null);
  const errorRef = useRef(null);
  const quantityRef = useRef(null);
  useEffect(() => {
    quantityRef.current?.focus({ preventScroll: true });
    quantityRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);
  const perform = async operation => {
    if (pending.current) return;
    pending.current = true; setBusy(true); onBusy?.(true); setError('');
    try { await operation(); onSaved(); }
    catch (failure) {
      const data = failure.response?.data;
      setError(typeof data === 'string' ? data : data?.message || t('Could not save consumption. Please retry.'));
      setTimeout(() => { errorRef.current?.focus(); errorRef.current?.scrollIntoView?.({ block: 'nearest' }); }, 0);
    } finally { pending.current = false; setBusy(false); onBusy?.(false); }
  };
  const add = () => {
    const amount = Number(quantity);
    if (!Number.isSafeInteger(amount) || amount < 1 || amount > 2147483647) {
      setError(t('Enter a positive whole number of shakes.')); return;
    }
    if (comment.length > 1000) {
      setError(t('Comment must be 1000 characters or fewer.')); return;
    }
    const normalizedComment = comment.trim() || null;
    // Retain this ID on ambiguous network failures; retrying must not charge twice.
    const payloadKey = `${customerId}:${date}:${amount}${normalizedComment ? `:${JSON.stringify(normalizedComment)}` : ''}`;
    const storageKey = `pending-additional-consumption:${payloadKey}`;
    if (request.current?.payloadKey !== payloadKey) {
      let id;
      try { id = sessionStorage.getItem(storageKey); } catch (_) { /* In-memory retry still works if storage is unavailable. */ }
      id = id || crypto.randomUUID();
      request.current = { payloadKey, id };
      try { sessionStorage.setItem(storageKey, id); } catch (_) { /* Retain the in-memory request ID. */ }
    }
    perform(async () => {
      await axios.post('/api/attendance/additional-consumption', { customerId, date, quantity: amount, comment: normalizedComment, requestId: request.current.id });
      try { sessionStorage.removeItem(storageKey); } catch (_) { /* No persisted request. */ }
      request.current = null;
    });
  };
  return <section className="additional-consumption-editor" aria-label={t('Additional consumption')}>
    <h3>{t('Additional consumption')} — {date}</h3>
    <p>{t('Guest shakes use the program covering this date. Each shake consumes one program serving; attendance is unchanged.')}</p>
    <label>{t('Number of extra shakes')} <input ref={quantityRef} type="number" min="1" step="1" value={quantity} disabled={busy} onChange={e => setQuantity(e.target.value)} /></label>
    <label className="additional-consumption-comment-label">{t('Comment (optional)')}
      <textarea rows={3} maxLength={1000} value={comment} disabled={busy}
        placeholder={t('Why was this consumption added?')} onChange={e => setComment(e.target.value)} />
    </label>
    <small>{comment.length}/1000</small>
    {error && <div ref={errorRef} role="alert" tabIndex={-1} className="alert alert-error">{error}</div>}
    <div className="attendance-selection-summary">
      <button className="btn btn-primary" disabled={busy} onClick={add}>{busy ? t('Saving...') : t('Add consumption')}</button>
      <button className="btn btn-secondary" disabled={busy} onClick={onClose}>{t('Close')}</button>
    </div>
    <ul>{entries.map(entry => <li key={entry.id}>
      +{entry.quantity} {t('shakes')} — {entry.reversed ? t('Reversed') : t('Recorded')}
      {entry.comment && <p className="additional-consumption-comment">{entry.comment}</p>}
      {!entry.reversed && entry.canUndo && <button className="btn btn-secondary" disabled={busy}
        onClick={() => perform(() => axios.post(`/api/attendance/additional-consumption/${entry.id}/undo`))}>
        {t('Undo')} +{entry.quantity}
      </button>}
    </li>)}</ul>
  </section>;
}
